import React, { useState, useRef, useEffect, useMemo, useCallback, useContext } from "react";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import InputBase from "@material-ui/core/InputBase";
import CloseIcon from "@material-ui/icons/Close";
import SendIcon from "@material-ui/icons/Send";
import StopIcon from "@material-ui/icons/StopRounded";
import MinimizeIcon from "@material-ui/icons/Remove";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeftRounded";
import MicIcon from "@material-ui/icons/MicNoneOutlined";
import ArrowFwdIcon from "@material-ui/icons/ArrowForwardRounded";
import CheckIcon from "@material-ui/icons/CheckCircleOutline";
import AddCommentOutlinedIcon from "@material-ui/icons/AddCommentOutlined";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ReplayIcon from "@material-ui/icons/Replay";

import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";

import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import Markdown from "../../components/Markdown";
import AskWidget from "../../components/AskWidget";
import { buildCatalog, buildSystemPrompt, buildContextPrompt, TOOLS } from "./copilot";
import { COLLECTOR_TOOLS } from "./collectorCopilot";
import { byType } from "../Connectors/catalog";
import { VERBS } from "./verbs";
import { SEEDS, hexA } from "../Chat/heroSeeds";
import { permissionFor, FLOW_EDIT_TOOLS, FLOW_EDIT_PERMISSION } from "./copilotPermissions";

const MENU_FONT = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
// tamanho da caixa flutuante do copiloto, escolhido pelo usuário (persiste entre sessões)
const LS_BOX = "numera.copilot.boxSize";
const DEFAULT_BOX = { w: 400, h: 600 };
// posição, guardada como distância até o canto inferior direito — mesma âncora do
// CSS (right/bottom), então mover e redimensionar não brigam entre si.
const LS_POS = "numera.copilot.boxPos";
const DEFAULT_POS = { right: 16, bottom: 16 };
// tempo limite por turno da IA — passado disto, mostra erro + "Tentar novamente"
const COPILOT_TIMEOUT_MS = 90000;
// streaming: o turno pode passar de 90s gerando sem problema (os bytes chegam aos
// poucos) — o teto aqui é só contra uma conexão pendurada
const COPILOT_STREAM_TIMEOUT_MS = 300000;

// POST /ai/chat em STREAMING (STU-160): o backend responde SSE com eventos
// { t:"text", d } (texto chegando), { t:"tool", name } (ação começando),
// { t:"thinking" }, e por fim { t:"done", out } com a MESMA resposta do modo
// normal (ou { t:"error", error }). Usa o axios (renovação de token) e lê o
// corpo aos poucos pelo onDownloadProgress.
const postChatStream = (body, { signal, onEvent }) =>
  new Promise((resolve, reject) => {
    let seen = 0;
    let buf = "";
    let final = null;
    let failure = null;
    const consume = text => {
      buf += text.slice(seen);
      seen = text.length;
      let i = buf.indexOf("\n\n");
      while (i >= 0) {
        const chunk = buf.slice(0, i);
        buf = buf.slice(i + 2);
        const data = chunk
          .split("\n")
          .filter(l => l.startsWith("data:"))
          .map(l => l.slice(5).trim())
          .join("");
        if (data) {
          let ev = null;
          try {
            ev = JSON.parse(data);
          } catch (e) {
            ev = null;
          }
          if (ev && ev.t === "done") final = ev.out;
          else if (ev && ev.t === "error") failure = ev.error || "Falha no copiloto.";
          else if (ev && onEvent) onEvent(ev);
        }
        i = buf.indexOf("\n\n");
      }
    };
    api
      .post("/ai/chat", { ...body, stream: true }, {
        signal,
        timeout: COPILOT_STREAM_TIMEOUT_MS,
        responseType: "text",
        transformResponse: [d => d], // texto cru (SSE), sem JSON.parse do axios
        onDownloadProgress: e => {
          const xhr = e && (e.currentTarget || (e.event && e.event.target) || e.target);
          if (xhr && typeof xhr.responseText === "string") consume(xhr.responseText);
        },
      })
      .then(res => {
        if (typeof res.data === "string") consume(res.data);
        if (failure) {
          const err = new Error(failure);
          err.response = { data: { error: failure } };
          reject(err);
        } else if (final) resolve(final);
        else reject(new Error("A resposta da IA veio incompleta. Tente de novo."));
      })
      .catch(err => {
        // erro antes do stream (402 de franquia, 400…) vem como JSON em texto
        if (err && err.response && typeof err.response.data === "string") {
          try {
            err.response.data = JSON.parse(err.response.data);
          } catch (e) {
            /* fica o texto */
          }
        }
        reject(err);
      });
  });

// Modal pra o copiloto criar um conector: o USUÁRIO digita as credenciais aqui
// (nunca passam pela IA). Ao salvar, POST /connectors e devolve o id + os verbos
// nativos daquele tipo (conn.<tipo>.*) para o copiloto montar os nós.
const ConnectorQuickModal = ({ type, onDone }) => {
  const cat = byType(type) || { name: type, fields: [] };
  const [name, setName] = useState(cat.name || type);
  const [cfg, setCfg] = useState({});
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const setField = (k, v) => setCfg(c => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      const { data } = await api.post("/connectors", { type, name: name || cat.name || type, config: cfg });
      const id = data && (data.id || (data.connector && data.connector.id));
      const verbs = Object.keys(VERBS)
        .filter(k => k.startsWith(`conn.${type}.`))
        .map(k => ({ key: k, title: VERBS[k].title }));
      onDone({ connectorId: id, connectorName: name || cat.name, verbs });
    } catch (e) {
      setErr((e && e.response && e.response.data && e.response.data.error) || e.message || "Falha ao salvar.");
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={() => (saving ? null : onDone({ canceled: true }))} maxWidth="xs" fullWidth>
      <DialogTitle>Configurar {cat.name || type}</DialogTitle>
      <DialogContent dividers>
        <div style={{ fontSize: 12.5, color: "#5B6470", marginBottom: 10 }}>
          O copiloto precisa deste conector para usar a ação nativa. As credenciais ficam só aqui — não passam pela IA.
        </div>
        <TextField label="Nome" value={name} onChange={e => setName(e.target.value)} fullWidth margin="dense" size="small" />
        {(cat.fields || []).map(f => (
          <TextField
            key={f.name}
            label={f.label || f.name}
            placeholder={f.placeholder || ""}
            type={f.secret ? "password" : "text"}
            value={cfg[f.name] || ""}
            onChange={e => setField(f.name, e.target.value)}
            fullWidth
            margin="dense"
            size="small"
          />
        ))}
        {err && <div style={{ color: "#C0453B", fontSize: 12.5, marginTop: 8 }}>{err}</div>}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onDone({ canceled: true })} disabled={saving}>
          Cancelar
        </Button>
        <Button onClick={save} disabled={saving} variant="contained" color="secondary">
          {saving ? "Salvando…" : "Salvar e usar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// formata contagem de tokens (1234 -> "1.2k")
const fmtK = n => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n || 0));

// custo estimado da sessão em R$ — Sonnet 5 (promo: US$2/1M entrada, US$10/1M
// saída; leitura de cache a 0,1x). Câmbio aproximado; ajuste PRICE se mudar o
// modelo/dólar. É estimativa (não conta o prêmio de 1,25x da gravação de cache).
const PRICE = { in: 2, out: 10, usdBrl: 5.4 };
const costBRL = u => {
  const usd =
    ((u.input || 0) * PRICE.in + (u.cached || 0) * PRICE.in * 0.1 + (u.output || 0) * PRICE.out) / 1e6;
  return usd * PRICE.usdBrl;
};
const fmtBRL = n => `R$ ${n.toFixed(n < 0.1 ? 3 : 2).replace(".", ",")}`;

// sparkle (✦) — acento de IA. Verde só sobre escuro (charcoal), nunca sobre branco.
const Sparkle = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden focusable="false">
    <path d="M12 1c.4 5.8 4.2 9.6 11 10-6.8.4-10.6 4.2-11 11-.4-6.8-4.2-10.6-11-11 6.8-.4 10.6-4.2 11-10z" />
  </svg>
);

const useStyles = makeStyles(theme => ({
  // FAB recolhido — círculo charcoal com sparkle verde, canto inf. direito
  fab: {
    position: "fixed", right: 16, bottom: 16, zIndex: 1300,
    width: 52, height: 52, borderRadius: "50%",
    background: theme.app.dark, color: theme.app.lime, border: "none", cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
    boxShadow: "0 8px 20px rgba(16,20,26,.28)",
    transition: "transform .12s ease, box-shadow .12s ease",
    "&:hover": { transform: "translateY(-1px)", boxShadow: "0 12px 26px rgba(16,20,26,.32)" },
  },
  // box aberto — flutuante sobre tudo, mesmo canto
  box: {
    position: "fixed", right: 16, bottom: 16, zIndex: 1300,
    width: 400, maxWidth: "92vw", height: "min(600px, 75vh)",
    background: "#fff", border: `1px solid ${theme.app.border}`, borderRadius: 14,
    boxShadow: "0 24px 60px rgba(16,20,26,.28)", overflow: "hidden",
    display: "flex", flexDirection: "column", fontFamily: MENU_FONT,
    animation: "$copilotIn .12s ease",
  },
  // Alças de redimensionar: 4 bordas + 4 cantos. Sem marca visual — o cursor já
  // comunica, e um triângulo fixo polui uma janela que o usuário move pela tela.
  //
  // Ficam INTEIRAS dentro da caixa (nada de offset negativo): o `.box` tem
  // overflow:hidden, então qualquer parte para fora é simplesmente cortada.
  // 8px é o alvo confortável sem invadir o conteúdo.
  rs: { position: "absolute", zIndex: 2, touchAction: "none" },
  rsN: { top: 0, left: 14, right: 14, height: 8, cursor: "ns-resize" },
  rsS: { bottom: 0, left: 14, right: 14, height: 8, cursor: "ns-resize" },
  rsW: { left: 0, top: 14, bottom: 14, width: 8, cursor: "ew-resize" },
  rsE: { right: 0, top: 14, bottom: 14, width: 8, cursor: "ew-resize" },
  // cantos por cima das bordas, senão a borda ganharia o clique na quina
  rsNW: { top: 0, left: 0, width: 14, height: 14, zIndex: 3, cursor: "nwse-resize" },
  rsNE: { top: 0, right: 0, width: 14, height: 14, zIndex: 3, cursor: "nesw-resize" },
  rsSW: { bottom: 0, left: 0, width: 14, height: 14, zIndex: 3, cursor: "nesw-resize" },
  rsSE: { bottom: 0, right: 0, width: 14, height: 14, zIndex: 3, cursor: "nwse-resize" },
  headDrag: { cursor: "grab", "&:active": { cursor: "grabbing" } },
  // modo embutido (drawer da tela de agentes): preenche o pai, sem float/sombra
  boxEmbedded: {
    position: "relative", width: "100%", height: "100%", maxWidth: "none",
    background: "#fff", overflow: "hidden",
    display: "flex", flexDirection: "column", fontFamily: MENU_FONT,
  },
  // modo COLUNA (tela de fluxo): coluna fixa, altura total, sem float/sombra/FAB e
  // sem minimizar/expandir/fechar. Abaixo de 1280 dá pra recolher (botão no header).
  // texto do modal de confirmação (permissões explícitas)
  approvalText: { fontSize: 14, lineHeight: 1.5, color: theme.app.ink },
  boxColumn: {
    position: "relative", width: 420, flexShrink: 0, height: "100%", maxWidth: "none",
    background: "#fff", overflow: "hidden", borderRight: `1px solid ${theme.app.border}`,
    display: "flex", flexDirection: "column", fontFamily: MENU_FONT,
    // transição da largura (hero 70% -> coluna normal): px->px anima suave; o canvas
    // (flexGrow) acompanha quadro a quadro. Respeita prefers-reduced-motion.
    transition: "width .55s cubic-bezier(.4,0,.2,1)",
    "@media (prefers-reduced-motion: reduce)": { transition: "none" },
    [theme.breakpoints.down("md")]: { width: 360 },
    [theme.breakpoints.down("sm")]: { width: "100%", borderRight: "none" },
  },
  // ── HERO embutido (nova automação): espelha a tela de nova conversa ──
  hero: { margin: "auto", width: "100%", maxWidth: 560, padding: "8px 8px 16px", boxSizing: "border-box" },
  heroSpark: {
    width: 42, height: 42, borderRadius: 12, background: theme.app.dark,
    display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 22,
    "& svg": { fontSize: 21, color: "#CEFF00" },
  },
  heroH1: { fontFamily: MENU_FONT, fontWeight: 900, fontSize: 28, letterSpacing: "-0.03em", lineHeight: 1.08, color: theme.app.ink, margin: 0 },
  heroLead: { fontSize: 14.5, color: theme.app.textSecondary, margin: "10px 0 0", lineHeight: 1.5 },
  heroField: {
    display: "flex", alignItems: "flex-end", gap: 8, marginTop: 22,
    border: `1px solid ${theme.app.border}`, borderRadius: 14, padding: "10px 10px 10px 16px",
    background: "#fff", boxShadow: "0 1px 2px rgba(16,24,40,.05)",
    transition: "border-color .12s, box-shadow .12s",
    "&:focus-within": { borderColor: theme.app.dark, boxShadow: "0 0 0 3px rgba(44,53,61,.08)" },
  },
  heroTextarea: {
    flex: 1, border: "none", outline: "none", resize: "none", fontFamily: MENU_FONT,
    fontSize: 15, lineHeight: 1.5, color: theme.app.ink, background: "transparent",
    maxHeight: 260, overflowY: "auto", padding: "7px 0", "&::placeholder": { color: theme.app.textTertiary },
  },
  heroSend: {
    flexShrink: 0, width: 40, height: 40, borderRadius: 11, border: "none",
    background: theme.app.dark, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer", transition: "background .12s", "&:hover": { background: "#1f262c" }, "& svg": { fontSize: 20 },
  },
  heroSendOff: { background: "#E2E4E7", color: "#A6ADB4", cursor: "default", "&:hover": { background: "#E2E4E7" } },
  seedsLabel: { fontSize: 12.5, color: theme.app.textTertiary, margin: "24px 0 10px" },
  seedList: { display: "flex", flexDirection: "column", gap: 10 },
  seedCard: {
    display: "flex", alignItems: "center", gap: 13, textAlign: "left", width: "100%",
    border: `1px solid ${theme.app.border}`, borderRadius: 14, padding: "13px 15px", background: "#fff",
    cursor: "pointer", fontFamily: MENU_FONT, transition: "border-color .12s, box-shadow .12s, transform .12s",
    "&:hover": { borderColor: "#D8DBDF", boxShadow: "0 4px 16px rgba(27,34,42,.06)" },
    "&:hover $seedArrow": { opacity: 1, transform: "none" },
  },
  seedIcon: { width: 36, height: 36, borderRadius: 10, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 20 } },
  seedText: { flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, color: theme.app.ink, lineHeight: 1.35 },
  seedArrow: { color: theme.app.textTertiary, flexShrink: 0, opacity: 0, transform: "translateX(-4px)", transition: "opacity .12s, transform .12s", "& svg": { fontSize: 20 } },
  heroManual: {
    display: "block", width: "100%", textAlign: "center", marginTop: 20, padding: 8,
    background: "none", border: "none", cursor: "pointer", fontFamily: MENU_FONT,
    fontSize: 13, fontWeight: 500, color: theme.app.textSecondary,
    "&:hover": { color: theme.app.ink },
  },
  // botão "recolher" o copiloto — só visível abaixo de 1280 (a única forma de esconder)
  collapseBtnNarrow: { display: "none", [theme.breakpoints.down("lg")]: { display: "inline-flex" } },
  // alça de redimensionar a coluna (borda direita) — sem marca visual, o cursor comunica
  colResize: { position: "absolute", top: 0, right: 0, bottom: 0, width: 6, cursor: "ew-resize", zIndex: 4, touchAction: "none", "&:hover": { background: theme.app.border } },
  "@keyframes copilotIn": {
    from: { opacity: 0, transform: "translateY(8px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  // entrada vinda da home: expande do próprio canto (continua o "minimizar pro canto")
  boxSeed: { animation: "$copilotSeedIn .46s cubic-bezier(.2,.8,.25,1) both", transformOrigin: "bottom right" },
  "@keyframes copilotSeedIn": {
    from: { opacity: 0, transform: "scale(.14)" },
    to: { opacity: 1, transform: "scale(1)" },
  },
  head: {
    flexShrink: 0, display: "flex", alignItems: "center", gap: 8,
    padding: "13px 15px", borderBottom: `1px solid ${theme.app.border}`,
  },
  avatar: { width: 26, height: 26, borderRadius: 7, background: theme.app.dark, color: theme.app.lime, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  title: { fontSize: 13.5, fontWeight: 600, color: theme.app.ink },
  ctxChip: { fontFamily: theme.app.mono, fontSize: 11, color: theme.app.textTertiary, background: theme.app.chip, padding: "2px 7px", borderRadius: 5 },
  headSpacer: { flex: 1 },
  headBtn: { color: theme.app.textTertiary, padding: 5, "& svg": { fontSize: 17 } },

  body: { flex: 1, overflowY: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 14, background: "#fff" },
  empty: { margin: "auto", textAlign: "center", color: theme.app.textSecondary, fontSize: 13, lineHeight: 1.6, padding: "8px 4px" },
  emptyTitle: { fontSize: 14, color: theme.app.ink, fontWeight: 600, marginBottom: 6 },
  // fade suave nas transições do empty-state (título/chips) — evita o snap
  emptyFade: { animation: "$emptyFadeIn .32s ease both" },
  "@keyframes emptyFadeIn": { from: { opacity: 0, transform: "translateY(4px)" }, to: { opacity: 1, transform: "translateY(0)" } },
  suggLoading: { fontSize: 12, color: theme.app.textTertiary, fontStyle: "italic", padding: "2px 4px" },

  // copiloto: avatar + texto em fundo transparente
  botRow: { display: "flex", gap: 9, alignItems: "flex-start" },
  botAvatar: { width: 24, height: 24, borderRadius: 7, background: theme.app.dark, color: theme.app.lime, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 },
  botMsg: {
    flex: 1, minWidth: 0, color: theme.app.ink, fontSize: 13.5, lineHeight: 1.5,
    "& .md > :first-child": { marginTop: 0 },
    "& .md-p": { margin: "0 0 8px" },
    "& .md-p:last-child": { marginBottom: 0 },
    "& .md-ul, & .md-ol": { margin: "4px 0 8px", paddingLeft: 20 },
    "& .md-ul li, & .md-ol li": { margin: "2px 0" },
    "& .md-h": { fontWeight: 600, margin: "8px 0 5px", lineHeight: 1.4 },
    "& .md-h1": { fontSize: 16 }, "& .md-h2": { fontSize: 15 }, "& .md-h3": { fontSize: 14 },
    "& .md-code": { fontFamily: theme.app.mono, fontSize: 12.2, background: "#F0F1F3", border: `1px solid ${theme.app.border}`, borderRadius: 5, padding: "1px 5px" },
    "& .md-pre": { background: theme.app.chatBg, color: theme.app.ink, border: `1px solid ${theme.app.border}`, borderRadius: 10, padding: "10px 12px", overflowX: "auto", margin: "4px 0 10px", fontFamily: theme.app.mono, fontSize: 12.2, lineHeight: 1.5 },
    "& .md-pre code": { background: "none", border: "none", padding: 0, color: "inherit" },
    "& a": { color: theme.app.info, textDecoration: "none" },
  },
  // usuário: bolha charcoal à direita
  // pergunta do usuário DISCRETA (cinza claro) — a resposta é que deve ter destaque
  userMsg: { alignSelf: "flex-end", maxWidth: "85%", background: theme.app.chip, color: theme.app.ink, borderRadius: "13px 13px 4px 13px", padding: "8px 12px", fontSize: 13, lineHeight: 1.5, whiteSpace: "pre-wrap" },

  // card de confirmação inline (ação do copiloto)
  card: { display: "flex", alignItems: "center", gap: 8, border: `1px solid ${theme.app.border}`, borderRadius: 9, padding: "9px 11px", fontSize: 12, color: theme.app.textSecondary },
  cardCheck: { color: theme.app.ok, fontSize: 16, flexShrink: 0 },
  cardDotErr: { width: 7, height: 7, borderRadius: "50%", background: theme.app.danger, flexShrink: 0 },
  cardErr: { color: theme.app.danger },
  err: { background: "#FBECEA", color: theme.app.danger, borderRadius: 9, padding: "9px 11px", fontSize: 12.5, border: `1px solid #F2C9C4`, display: "flex", flexDirection: "column", gap: 7, alignItems: "flex-start" },
  errRetry: {
    display: "inline-flex", alignItems: "center", gap: 4, background: "#fff", color: theme.app.danger,
    border: "1px solid #E4B3AD", borderRadius: 7, padding: "4px 9px", fontSize: 12, fontWeight: 600,
    cursor: "pointer", fontFamily: "inherit", "& svg": { fontSize: 15 },
    "&:hover": { background: "#FDF3F1" }, "&:disabled": { opacity: 0.5, cursor: "default" },
  },

  // grupo de ações (mini-cards) colapsado numa linha única + expandir
  toolGroup: { display: "flex", flexDirection: "column", gap: 6 },
  toolGroupHead: { display: "flex", alignItems: "center", gap: 3, border: "none", background: "none", cursor: "pointer", color: theme.app.textSecondary, fontSize: 12, fontWeight: 600, fontFamily: MENU_FONT, padding: 0 },
  toolCollapsed: { width: "100%", cursor: "pointer", fontFamily: MENU_FONT, fontSize: 12, textAlign: "left", background: "#fff", color: theme.app.textSecondary, "&:hover": { background: theme.app.hover } },
  toolCollapsedText: { flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  toolCount: { fontFamily: theme.app.mono, fontSize: 11, color: theme.app.textTertiary, background: theme.app.chip, padding: "1px 6px", borderRadius: 9, flexShrink: 0 },
  toolChevron: { fontSize: 17, color: theme.app.textTertiary, flexShrink: 0 },
  // três pontinhos em animação (indicador "trabalhando" na própria linha da ação)
  "@keyframes nbBlink": { "0%, 80%, 100%": { opacity: 0.2 }, "40%": { opacity: 1 } },
  dots: {
    display: "inline-flex", gap: 2, marginLeft: 3, verticalAlign: "middle",
    "& span": { width: 3, height: 3, borderRadius: "50%", background: "currentColor", animation: "$nbBlink 1.2s infinite ease-in-out" },
    "& span:nth-child(2)": { animationDelay: ".2s" },
    "& span:nth-child(3)": { animationDelay: ".4s" },
  },
  // status rotativo do "pensando" (frase + pontinhos) — dá vida à espera
  thinking: { display: "inline-flex", alignItems: "center", fontSize: 13, color: theme.app.textSecondary, fontFamily: MENU_FONT },
  "@keyframes nbWordIn": { from: { opacity: 0, transform: "translateY(2px)" }, to: { opacity: 1, transform: "none" } },
  thinkingWord: { animation: "$nbWordIn .28s ease both" },
  thinkingTime: { marginLeft: 6, color: theme.app.textTertiary, fontVariantNumeric: "tabular-nums" },

  // ações da IA como LOG discreto (sem moldura): linha vertical fina ligando as
  // ações, indentadas sob a fala da IA. Colapsa para um resumo ao concluir.
  actWrap: { marginLeft: 16, paddingLeft: 8, borderLeft: `2px solid ${theme.app.lineSoft}`, display: "flex", flexDirection: "column", gap: 1 },
  actLine: { display: "flex", alignItems: "center", gap: 8, padding: "3px 7px", borderRadius: 7, minWidth: 0, "&:hover": { background: theme.app.hover } },
  actIcon: { width: 16, height: 16, flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", color: theme.app.alt },
  actCheck: { fontSize: 16, color: theme.app.ok },
  actX: { fontSize: 16, color: theme.app.danger },
  actLabel: { fontSize: 13, color: theme.app.textSecondary, fontFamily: MENU_FONT, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  actNode: { fontFamily: theme.app.mono, fontSize: 12, color: theme.app.ink, flexShrink: 0 },
  actErr: { fontSize: 12, color: theme.app.danger || "#C0453B", fontFamily: MENU_FONT, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  actCount: { fontFamily: theme.app.mono, fontSize: 11, color: theme.app.textTertiary, flexShrink: 0 },
  actSummary: { display: "flex", alignItems: "center", gap: 8, width: "100%", marginLeft: 16, padding: "3px 6px 3px 17px", background: "none", border: "none", cursor: "pointer", borderRadius: 7, textAlign: "left", fontFamily: MENU_FONT, "&:hover": { background: theme.app.hover } },
  actSummaryText: { flex: 1, minWidth: 0, fontSize: 13, color: theme.app.textSecondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  actChevron: { fontSize: 17, color: theme.app.textTertiary, flexShrink: 0 },

  // sugestões (chips) acima do input
  chips: { flexShrink: 0, display: "flex", flexWrap: "wrap", gap: 7, padding: "0 16px 10px" },
  chip: { border: `1px solid ${theme.app.border}`, background: "#fff", borderRadius: 16, padding: "6px 11px", fontSize: 12, color: theme.app.textSecondary, cursor: "pointer", fontFamily: MENU_FONT, textAlign: "left", lineHeight: 1.3, "&:hover": { background: theme.app.hover } },

  // input fixo no rodapé
  foot: { flexShrink: 0, padding: "10px 14px 12px" },
  // uma linha só (sem quebrar), à esquerda; só staff vê
  tokLine: { fontSize: 11, color: theme.app.textTertiary, fontFamily: theme.app.mono, textAlign: "left", marginBottom: 8, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  // campo destacado do painel: borda hairline, raio 16, fundo levemente diferente;
  // no FOCO a borda vira ink (sem anel verde — baixo contraste no branco).
  inputRow: { display: "flex", alignItems: "flex-end", gap: 8, border: "1px solid #D1D5DB", borderRadius: 16, padding: "9px 8px 9px 12px", background: theme.app.hover, transition: "border-color .12s, background .12s", "&:focus-within": { borderColor: theme.app.ink, background: "#fff" } },
  // pulso de atenção (ao vir do "Criar com IA"): anel que expande e some, 2 ciclos,
  // puxando o olhar pra caixa. Charcoal/ink (anel verde no branco tem baixo contraste).
  "@keyframes copilotInputPulse": {
    "0%": { boxShadow: "0 0 0 0 rgba(27,34,42,0.38)" },
    "70%": { boxShadow: "0 0 0 9px rgba(27,34,42,0)" },
    "100%": { boxShadow: "0 0 0 0 rgba(27,34,42,0)" },
  },
  inputRowPulse: { borderColor: theme.app.ink, background: "#fff", animation: "$copilotInputPulse 1s ease-out 2" },
  micIcon: { color: theme.app.textTertiary, fontSize: 19, marginBottom: 2, flexShrink: 0 },
  input: { flex: 1, fontSize: 13.5, fontFamily: MENU_FONT, maxHeight: 184, overflowY: "auto" },
  // vazio → cinza (parece "espere"); com texto → INK + seta branca (dá p/ enviar)
  send: { width: 30, height: 30, borderRadius: 8, background: theme.app.ink, color: "#fff", padding: 0, "&:hover": { background: "#0f141a" }, "&.Mui-disabled": { background: theme.app.chip, color: theme.app.textTertiary } },
  enterHint: { fontSize: 11, color: theme.app.textTertiary, marginTop: 6, paddingLeft: 4 },
  // botão STOP separado e visualmente distinto do enviar (branco + borda + ícone
  // vermelho) — evita apertar "parar" achando que é "enviar" durante o processamento.
  stop: { width: 30, height: 30, borderRadius: 7, background: "#fff", border: `1px solid ${theme.app.border}`, color: theme.app.danger, padding: 0, marginRight: 2, "&:hover": { background: theme.app.hover, borderColor: theme.app.danger } },
}));

// três pontinhos em animação
const Dots = () => {
  const classes = useStyles();
  return (
    <span className={classes.dots}>
      <span />
      <span />
      <span />
    </span>
  );
};

// frases de AQUECIMENTO — enquanto a IA ainda não devolveu nada.
// UMA frase por interação: sorteada no início da espera e mantida (não rotaciona).
// Mescla séria/leve com PESO 70/30 (ver ThinkingStatus): a maioria descreve o estado
// real (recebendo/preparando); a minoria é leve. Depois a montagem aparece nó a nó,
// com as mensagens próprias de cada ação (não fingem que já constroem aqui).
const SERIOUS_PHRASES = [
  "Recebendo o seu pedido",
  "Lendo o que você escreveu",
  "Entendendo o que você precisa",
  "Só um instante",
  "Preparando pra começar",
  "Já vamos começar",
  "Organizando os próximos passos",
  "Carregando o necessário",
  "Pegando o contexto",
  "Deixando tudo pronto pra começar",
  "Entrando no assunto",
  "Analisando o pedido",
  "Puxando os recursos",
  "Um momento",
  "Montando o plano",
  "Prestes a começar",
  "Reunindo o que precisa",
  "Dando a partida",
  "Verificando os detalhes",
  "Iniciando",
];
const LIGHT_PHRASES = [
  "Arregaçando as mangas",
  "Respirando fundo",
  "Estalando os dedos",
  "Sentando pra trabalhar",
  "Pegando as ferramentas",
  "Colocando a mão na massa",
  "Afiando o lápis",
  "Pondo o pensamento pra rodar",
];

const fmtElapsed = s => (s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`);

const ThinkingStatus = ({ label, onShown }) => {
  const classes = useStyles();
  // UMA frase por interação: sorteada no início da espera e mantida até a IA
  // responder (não rotaciona mais uma sequência de mensagens). Peso 70/30:
  // 70% das vezes uma frase séria (estado real), 30% uma leve.
  const [phrase] = useState(() => {
    const pool = Math.random() < 0.7 ? SERIOUS_PHRASES : LIGHT_PHRASES;
    return pool[Math.floor(Math.random() * pool.length)];
  });
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const started = Date.now();
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => clearInterval(t);
  }, []);
  // avisa o painel do que está escrito (o nó provisório do canvas mostra o mesmo)
  const shown = label || phrase;
  useEffect(() => {
    if (onShown) onShown(shown);
  }, [shown, onShown]);
  return (
    <span className={classes.thinking}>
      {/* streaming: a ação que a IA começou a escrever (ex.: "Adicionando nó") */}
      <span className={classes.thinkingWord}>{label || phrase}</span>
      <Dots />
      {elapsed > 0 && <span className={classes.thinkingTime}>· {fmtElapsed(elapsed)}</span>}
    </span>
  );
};

// ações da IA como LOG DISCRETO: durante a execução, cada ação é uma linha sem
// moldura (ícone de status + label apagado + nome do nó em mono), ligadas por uma
// linha vertical fina sob a fala da IA. Ao concluir, colapsa para uma linha-resumo
// (✓ Fluxo atualizado · N nós) expansível. `live` = rodando agora.
const ToolGroup = ({ tools, live }) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);
  const anyErr = tools.some(t => t.ok === false);
  const n = tools.length;

  const line = (t, i) => (
    <div key={i} className={classes.actLine}>
      <span className={classes.actIcon}>
        {t.ok === false ? <CloseIcon className={classes.actX} /> : <CheckIcon className={classes.actCheck} />}
      </span>
      <span className={classes.actLabel}>{t.text}</span>
      {t.node ? <span className={classes.actNode}>{t.node}</span> : null}
      {t.ok === false && t.error ? <span className={classes.actErr} title={t.error}>{t.error}</span> : null}
    </div>
  );

  const summary = (onClick, Chevron) => (
    <button type="button" className={classes.actSummary} onClick={onClick}>
      <span className={classes.actIcon}>
        {anyErr ? <CloseIcon className={classes.actX} /> : <CheckIcon className={classes.actCheck} />}
      </span>
      <span className={classes.actSummaryText}>
        {anyErr ? "Concluído com erros" : "Fluxo atualizado"}{" "}
        <span className={classes.actCount}>· {n} {n === 1 ? "ação" : "ações"}</span>
      </span>
      <Chevron className={classes.actChevron} />
    </button>
  );

  // rodando → log ao vivo (ações concluídas + pulso de "trabalhando")
  if (live) {
    return (
      <div className={classes.actWrap}>
        {tools.map(line)}
        <div className={classes.actLine}>
          <span className={classes.actIcon}><Dots /></span>
          <span className={classes.actLabel}>Trabalhando</span>
        </div>
      </div>
    );
  }

  // concluído → resumo colapsado; expandido → resumo + todas as ações
  if (!expanded) return summary(() => setExpanded(true), ExpandMoreIcon);
  return (
    <div>
      {summary(() => setExpanded(false), ExpandLessIcon)}
      <div className={classes.actWrap}>{tools.map(line)}</div>
    </div>
  );
};

// texto curto do erro de uma ferramenta (mostrado no passo e gravado no log)
const errorText = r => {
  if (!r) return "falhou";
  const e = r.error || r.message || (Array.isArray(r.errors) && r.errors.map(x => (x && x.message) || x).join("; ")) || (Array.isArray(r.blockingIssues) && r.blockingIssues.map(x => (x && x.message) || x).join("; "));
  return String(e || "falhou").slice(0, 300);
};

const TOOL_LABEL = {
  // fluxo / automação
  read_flow: "Lendo o fluxo",
  set_trigger: "Definindo gatilho",
  set_notes: "Escrevendo as notas do fluxo",
  add_node: "Adicionando nó",
  connect: "Conectando",
  update_node: "Ajustando nó",
  delete_node: "Removendo nó",
  validate_flow: "Validando o fluxo",
  set_name: "Nomeando o fluxo",
  publish_flow: "Publicando",
  place_call: "Discando",
  propose_whatsapp_template: "Preparando o modelo de WhatsApp",
  // conexões
  create_connector: "Configurando a conexão",
  introspect_schema: "Lendo a estrutura do banco",
  run_sample_query: "Consultando uma amostra dos dados",
  save_schema_note: "Anotando o que aprendeu do banco",
  // coletor de dados
  create_collector: "Montando o coletor",
  test_collector: "Testando o coletor",
  publish_collector: "Publicando o coletor",
  reset_collector: "Resetando a leitura do coletor",
  // handoff entre agentes (bastidores)
  transfer_to_collector: "Preparando a criação do coletor",
  transfer_to_automation: "Preparando a automação",
  transfer_to_data: "Preparando dados e relatórios",
  transfer_to_agents: "Preparando o agente de IA",
  get_agent: "Lendo o agente",
  create_agent: "Criando o agente",
  update_agent: "Ajustando o agente",
  create_agent_event: "Configurando evento do agente",
  test_connector: "Testando o conector",
  transfer_back: "Retomando o atendimento",
  // dados & relatórios
  create_dataset: "Criando a tabela",
  query_dataset: "Consultando os dados",
  create_report: "Salvando o relatório",
  // memória
  remember: "Guardando na memória",
  ask_user: "Perguntando um detalhe",
};

const EXAMPLES = [
  "Crie uma URA que diz 'bem-vindo' e oferece 1 para vendas e 2 para suporte.",
  "Quando entrar uma chamada, transcreva o que a pessoa falar e mande por WhatsApp.",
  "Atenda a chamada, toque uma saudação e transfira para o ramal 1001.",
];

// respostas rápidas durante a conversa: ações comuns em 1 clique (em vez de digitar)
const QUICK_REPLIES = [
  { label: "Continuar", prompt: "continue" },
  { label: "Validar o fluxo", prompt: "Valide o fluxo e aponte os problemas." },
  { label: "Publicar", prompt: "Publique o fluxo." },
];

// PERFIL padrão: copiloto de FLUXO (o comportamento de hoje). O painel é genérico
// e recebe o perfil por prop `profile`; o copiloto de AGENTE passa outro perfil e
// reusa o MESMO componente/UI. system/tools são calculados por TURNO a partir do
// contexto + `extra` (estado mutável do perfil, ex.: desbloqueio de ferramentas).
const flowProfile = {
  title: "Copiloto",
  ctxChip: "este fluxo",
  emptyTitle: "Descreva o fluxo que você quer",
  emptyDesc: "Eu monto os nós no canvas pra você. Depois é só revisar e Publicar.",
  examples: EXAMPLES,
  quickReplies: QUICK_REPLIES,
  placeholder: "Peça uma alteração…",
  initialExtra: {},
  loadContext: async projectId => {
    const safe = async p => p.then(r => r.data).catch(() => null);
    const [nums, conns, apps, auds] = await Promise.all([
      safe(api.get("/numbers")),
      safe(api.get("/connectors")),
      safe(api.get("/applications", { params: projectId ? { projectId } : {} })),
      safe(api.get("/audio/library")),
    ]);
    const arr = (d, k) => (Array.isArray(d) ? d : (d && d[k]) || []);
    return {
      numbers: arr(nums, "numbers").map(n => ({ id: n.id, number: n.number })).filter(n => n.number).slice(0, 30),
      connectors: arr(conns, "connectors").map(c => ({ id: c.id, name: c.name, type: c.type })).slice(0, 30),
      agents: arr(apps, "applications").filter(a => a.triggerType === "agent").map(a => ({ id: a.id, name: a.name })).slice(0, 30),
      audios: arr(auds, "assets").map(a => ({ id: a.id, name: a.name })).slice(0, 40),
    };
  },
  buildSystem: (ctx, extra, opts) => buildSystemPrompt(buildCatalog(ctx), buildContextPrompt(ctx), { demo: opts.demo }),
  buildTools: (ctx, extra, opts) => (opts.demo ? TOOLS.filter(t => t.name !== "create_connector") : TOOLS),
};

// Perfil do copiloto DENTRO do canvas de um COLETOR: é o MESMO copiloto de fluxo
// (todas as ferramentas do canvas) MAIS o conhecimento de coletor — pode explorar
// o banco (introspect_schema / run_sample_query / save_schema_note), igual ao
// assistente flutuante. A EXECUÇÃO dessas 3 ferramentas já é feita pelo Builder
// (delega ao backend); aqui só as EXPOMOS ao modelo e ensinamos a usá-las + o
// contrato do coletor (query/cursor). NÃO inclui create/test/publish_collector:
// no canvas o fluxo já existe e se edita por add_node/update_node + publish_flow.
const DB_TOOLS = COLLECTOR_TOOLS.filter(t =>
  ["introspect_schema", "run_sample_query", "save_schema_note", "reset_collector"].includes(t.name)
);

const COLLECTOR_KNOWLEDGE = [
  "",
  "── VOCÊ ESTÁ EDITANDO UM COLETOR DE DADOS ──",
  "Um coletor varre uma fonte (MySQL) no intervalo definido e entrega só os registros",
  "NOVOS, via um cursor. O canvas tem a Consulta MySQL + o nó 'Retornar ao coletor'",
  "(que define o cursorField). O connectorId da conexão MySQL está no nó Consulta MySQL",
  "(use read_flow) e nas conexões do contexto.",
  "",
  "Você PODE explorar o banco conectado — NÃO adivinhe nomes de tabela/coluna:",
  "1. introspect_schema(connectorId) — tabelas/colunas/tipos/FKs + as NOTAS já salvas",
  "   sobre este banco (memória): use-as, não repergunte o que já está lá.",
  "2. run_sample_query(connectorId, query) — roda um SELECT read-only e traz algumas",
  "   linhas, pra CONFIRMAR com dados reais a tabela/campo certos (ask_user quando",
  "   precisar que o usuário confirme). Só SELECT.",
  "3. save_schema_note(connectorId, note) — grave uma descoberta durável (qual tabela",
  "   é o quê, qual é o cursor, um join, o significado de um status). Nunca salve dados",
  "   de linhas, só o entendimento do banco.",
  "",
  "Para montar/ajustar o coletor, edite os nós (update_node): a query no nó Consulta",
  "MySQL e o cursorField no nó 'Retornar ao coletor'. CONTRATO da query:",
  "- UM SELECT read-only (JOINs ok), sem ponto e vírgula.",
  "- NOMES DE COLUNA ÚNICOS no resultado (alias quando repetir: c.nome AS cliente_nome).",
  "- cursorField = coluna PRESENTE no SELECT e crescente para linhas novas (id PK da",
  "  tabela principal; senão uma data/hora de CRIAÇÃO — nunca um campo que é atualizado).",
  "- A plataforma já envolve seu SELECT com o cursor + LIMIT automaticamente: NÃO",
  "  adicione ORDER BY/LIMIT de paginação por conta própria.",
  "- Traga os campos que serão usados depois (ex.: inclua o email/telefone do cliente via JOIN).",
  "",
  "RESET: o 'até onde já li' (cursor) NÃO fica no desenho do fluxo — é estado salvo à",
  "parte. Se o usuário quer recomeçar do zero/reprocessar, chame reset_collector (ele",
  "opera sobre ESTE coletor). AVISE antes que isso REDISPARA as automações ligadas",
  "(pode reenviar/religar) e confirme (ask_user) quando o consumidor liga/envia.",
].join("\n");

export const collectorFlowProfile = {
  ...flowProfile,
  emptyTitle: "Descreva o coletor que você quer",
  emptyDesc: "Eu exploro seu banco e monto o coletor no canvas. Depois é só revisar e Publicar.",
  buildSystem: (ctx, extra, opts) =>
    buildSystemPrompt(buildCatalog(ctx), buildContextPrompt(ctx), { demo: opts.demo }) + "\n" + COLLECTOR_KNOWLEDGE,
  buildTools: (ctx, extra, opts) => {
    const base = opts.demo ? TOOLS.filter(t => t.name !== "create_connector") : TOOLS;
    return [...base, ...DB_TOOLS];
  },
};

// CONSERTA a conversa antes de cada envio: a API exige que TODO tool_use do
// assistente tenha o tool_result na mensagem SEGUINTE. Isso quebrava (HTTP 400
// "tool_use ids were found without tool_result") quando a resposta de uma pergunta
// (ask_user) chegava fora de ordem — ex.: outra chamada saiu enquanto a pergunta
// estava aberta — ou o turno parou no meio. Regras:
// - tool_use sem resultado logo depois → ganha um tool_result "skipped";
// - tool_result que não responde ao assistente imediatamente anterior (órfão) →
//   vira texto, para a IA não perder o que o usuário respondeu.
const sanitizeConv = conv => {
  if (!Array.isArray(conv) || !conv.length) return conv || [];
  const out = [];
  let prevIds = null; // ids de tool_use do assistente imediatamente anterior
  conv.forEach(m => {
    if (!m) return;
    if (m.role === "assistant") {
      // assistente com tool_use pendente e logo outro assistente: fecha o anterior
      if (prevIds && prevIds.size) out.push({ role: "user", content: [...prevIds].map(id => ({ type: "tool_result", tool_use_id: id, content: JSON.stringify({ skipped: true }) })) });
      out.push(m);
      prevIds = new Set(Array.isArray(m.content) ? m.content.filter(b => b && b.type === "tool_use").map(b => b.id) : []);
      return;
    }
    // usuário
    const blocks = Array.isArray(m.content) ? m.content : null;
    const results = [];
    const rest = [];
    if (blocks) {
      blocks.forEach(b => {
        if (b && b.type === "tool_result") {
          if (prevIds && prevIds.has(b.tool_use_id)) {
            results.push(b);
            prevIds.delete(b.tool_use_id);
          } else {
            // órfão: preserva o conteúdo como texto
            let txt = b.content;
            try { const j = JSON.parse(b.content); if (j && j.answer != null) txt = `Resposta à sua pergunta anterior: ${Array.isArray(j.answer) ? j.answer.join(", ") : j.answer}`; } catch (e) { /* texto cru */ }
            rest.push({ type: "text", text: String(txt || "") });
          }
        } else if (b) rest.push(b);
      });
    } else rest.push({ type: "text", text: String(m.content == null ? "" : m.content) });
    // o que ainda faltou do assistente anterior → skipped (antes de qualquer texto)
    const missing = prevIds ? [...prevIds].map(id => ({ type: "tool_result", tool_use_id: id, content: JSON.stringify({ skipped: true }) })) : [];
    prevIds = null;
    const content = [...results, ...missing, ...rest.filter(b => b.type !== "text" || String(b.text || "").trim())];
    if (!content.length) return;
    // mensagem só de texto simples volta a ser string (como o resto do código grava)
    if (!blocks && content.length === 1 && content[0].type === "text") out.push({ role: "user", content: content[0].text });
    else out.push({ role: "user", content });
  });
  // último assistente com tool_use pendente (parou/recarregou no meio)
  if (prevIds && prevIds.size) out.push({ role: "user", content: [...prevIds].map(id => ({ type: "tool_result", tool_use_id: id, content: JSON.stringify({ skipped: true }) })) });
  return out;
};

// persistência da conversa por app (localStorage) — restaura ao reabrir/recarregar
const convKey = appId => (appId ? `nb_copilot_${appId}` : "nb_copilot_new");
const loadConv = appId => {
  try {
    const raw = localStorage.getItem(convKey(appId));
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (p && Array.isArray(p.msgs) && Array.isArray(p.conv)) return { msgs: p.msgs, conv: sanitizeConv(p.conv), usage: p.usage && typeof p.usage === "object" ? p.usage : { input: 0, output: 0, cached: 0 } };
  } catch (e) {
    /* corrompido — ignora */
  }
  return null;
};

const CopilotPanel = ({ open, onClose, onOpen, runTool, onAfterRun, onBusyChange, projectId, appId, seed, demo, profile = flowProfile, embedded, column, flowSummary, flowLoading, pulse, hero, widthOverride, onManual, initialAgent, onStatus, flowGuard }) => {
  // "inline" = não-flutuante: modo drawer (embedded) ou coluna fixa (column). Sem
  // FAB, sem arrastar/redimensionar; quem controla o tamanho é o pai/layout.
  const inline = embedded || column;
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  // linha de tokens/custo da sessão: só STAFF da Numera (o backend também não manda
  // o `usage` no payload p/ não-staff — dupla proteção).
  const canSeeTokens = !!(user && user.isStaff);
  const saved = useRef(null); // snapshot carregado do localStorage (1x por appId)
  // só lê de forma síncrona se já temos o appId real; em apps existentes ele
  // chega null no 1º render (o numericId do Builder resolve depois) — nesse caso
  // a hidratação acontece no efeito abaixo quando o id chega.
  if (saved.current === null) saved.current = (appId ? loadConv(appId) : null) || { msgs: [], conv: [], usage: { input: 0, output: 0, cached: 0 } };
  const [msgs, setMsgs] = useState(saved.current.msgs); // display: {role:'user'|'bot'|'tool'|'err', text, ok}
  const [usage, setUsage] = useState(saved.current.usage || { input: 0, output: 0, cached: 0 }); // tokens acumulados na sessão
  const [busy, setBusy] = useState(false);
  const [input, setInput] = useState("");
  const [boxSize, setBoxSize] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(LS_BOX) || "null");
      // valida antes de confiar: localStorage é editável e um NaN aqui quebraria o layout
      if (raw && Number.isFinite(raw.w) && Number.isFinite(raw.h)) return raw;
    } catch (e) { /* json inválido / modo privado */ }
    return DEFAULT_BOX;
  });
  const [boxPos, setBoxPos] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(LS_POS) || "null");
      if (raw && Number.isFinite(raw.right) && Number.isFinite(raw.bottom)) return raw;
    } catch (e) { /* json inválido / modo privado */ }
    return DEFAULT_POS;
  });
  // largura da COLUNA (modo column) — redimensionável pela borda direita, persistida.
  const [colW, setColW] = useState(() => {
    try { const v = Number(localStorage.getItem("nb_copilot_col_w")); if (v >= 300 && v <= 760) return v; } catch (e) { /* ignore */ }
    return 420;
  });
  // sugestões CONTEXTUAIS (próximos passos do fluxo aberto) p/ os chips do empty-state
  const [dynExamples, setDynExamples] = useState(null);
  const suggestedRef = useRef(null); // appId p/ o qual já buscamos (evita refetch)
  // dica "Enter envia · Shift+Enter quebra linha" — só na 1ª vez
  const [enterHintSeen, setEnterHintSeen] = useState(() => { try { return localStorage.getItem("nb_copilot_enterhint") === "1"; } catch (e) { return false; } });
  const [ctx, setCtx] = useState(null); // recursos reais da conta (números/conectores/agentes/áudios)
  const convRef = useRef(saved.current.conv); // provider messages (Anthropic-style blocks)
  const bodyRef = useRef(null);
  const abortRef = useRef(null); // AbortController da requisição em voo
  const stoppedRef = useRef(false); // sinaliza "parar" o loop agêntico
  const failCountRef = useRef({}); // falhas SEGUIDAS por ferramenta (disjuntor)
  const structuralRef = useRef(false); // o turno mexeu na estrutura do fluxo (nós/arestas)
  const [pendingAsk, setPendingAsk] = useState(null); // pergunta estruturada em aberto (ask_user)
  const askResolveRef = useRef(null); // resolve da Promise que aguarda a resposta
  const [pendingConn, setPendingConn] = useState(null); // conector a configurar (create_connector)
  const connResolveRef = useRef(null); // resolve da Promise que aguarda o modal do conector
  const [pendingApproval, setPendingApproval] = useState(null); // ação aguardando o "Confirmar" do usuário
  const [live, setLive] = useState({ text: "", tool: null }); // o que a IA está gerando AGORA (streaming)
  const [thinkingShown, setThinkingShown] = useState(""); // frase do "pensando" na tela
  const approvalResolveRef = useRef(null); // resolve da Promise que aguarda a confirmação
  const inputRef = useRef(null); // input do chat — foco automático ao abrir o painel
  // campo grande do hero ("O que você quer montar?"): cresce com o texto, até ~10
  // linhas, e depois rola (STU-177)
  const heroInputRef = useRef(null);
  useEffect(() => {
    const ta = heroInputRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, 260)}px`;
  }, [input, hero]);
  // ao ABRIR o painel, coloca o cursor no input (ex.: "Criar com IA" já pronto pra digitar)
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => { try { inputRef.current && inputRef.current.focus(); } catch (e) { /* ignore */ } }, 90);
    return () => clearTimeout(t);
  }, [open]);
  // PULSO de atenção: disparado pelo "Criar com IA" (prop `pulse` > 0). Dá um glow
  // na caixa + foco, pra o olhar do usuário cair direto no input do copiloto.
  const [pulsing, setPulsing] = useState(false);
  useEffect(() => {
    if (!pulse) return undefined; // 0 = sem pulso (abertura normal pelo trilho)
    setPulsing(true);
    const tf = setTimeout(() => { try { inputRef.current && inputRef.current.focus(); } catch (e) { /* ignore */ } }, 120);
    const t = setTimeout(() => setPulsing(false), 2100); // 2 ciclos de ~1s
    return () => { clearTimeout(tf); clearTimeout(t); };
  }, [pulse]);

  // (re)carrega o contexto da conta TODA vez que o painel abre (ou muda o projeto)
  // — para o modelo enxergar recursos criados depois (números/conectores/agentes/
  // áudios), inclusive as ações conn.* do catálogo (item 57). `cancelled` evita
  // setState de um fetch que ficou pra trás (painel fechado/reaberto no meio).
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      const c = await profile.loadContext(projectId);
      if (!cancelled) setCtx(c || {});
    })();
    return () => {
      cancelled = true;
    };
  }, [open, projectId, profile]);

  // ctx/extra em refs para o loop calcular system+tools por TURNO com valor estável.
  // `extra` é o estado mutável do perfil (ex.: desbloqueio de ferramentas do agente);
  // uma tool pode devolver { __extra } para mudar as ferramentas do próximo turno.
  const ctxRef = useRef(null);
  ctxRef.current = ctx;
  // especialista inicial: o do perfil, ou o que a tela pedir (ex.: fluxo vazio →
  // direto no de automação, sem passar pela triagem — STU-160)
  const baseExtra = () => (initialAgent ? { ...(profile.initialExtra || {}), activeAgent: initialAgent } : profile.initialExtra || {});
  const extraRef = useRef(baseExtra());

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [msgs, busy]);

  // avisa o pai quando o copiloto está "escrevendo" (ex.: o editor de persona
  // entra em leitura durante o build)
  useEffect(() => {
    if (onBusyChange) onBusyChange(busy);
  }, [busy, onBusyChange]);

  // app "new" vira id numérico após salvar: migra a conversa em memória para a
  // chave definitiva, sem perder o histórico já construído
  const prevAppId = useRef(appId);
  const seedEnteredRef = useRef(false); // já tocou a entrada "expande do canto"?
  useEffect(() => { if (open && seed) { const t = setTimeout(() => { seedEnteredRef.current = true; }, 500); return () => clearTimeout(t); } return undefined; }, [open, seed]);
  // Ao ABRIR num fluxo EXISTENTE com conversa vazia, faz uma leitura rápida do fluxo
  // (resumo) e traz sugestões RELACIONADAS ao que ele faz — em vez de exemplos
  // genéricos. One-shot por app (best-effort: falhou → segue com os genéricos).
  useEffect(() => {
    if (!open || embedded || seed) return;
    if (msgs.length > 0 || !appId || !flowSummary) return;
    if (suggestedRef.current === appId) return; // um fetch por abertura (por app)
    suggestedRef.current = appId;
    // CACHE por app: só chama a IA se o RESUMO do fluxo mudou desde a última geração
    // (a app foi modificada). Igual → reusa o cacheado, sem chamar a IA.
    try {
      const c = JSON.parse(localStorage.getItem(`nb_copilot_sugg_${appId}`) || "null");
      if (c && c.summary === flowSummary && Array.isArray(c.suggestions) && c.suggestions.length) {
        setDynExamples(c.suggestions);
        return;
      }
    } catch (e) { /* ignore */ }
    api
      .post("/ai/suggest-actions", { summary: flowSummary })
      .then(({ data }) => {
        const s = (data && data.suggestions) || [];
        setDynExamples(s); // [] (resolvido vazio) → cai nos exemplos genéricos
        if (s.length) { try { localStorage.setItem(`nb_copilot_sugg_${appId}`, JSON.stringify({ summary: flowSummary, suggestions: s })); } catch (e) { /* ignore */ } }
      })
      .catch(() => setDynExamples([])); // falhou → genéricos
  }, [open, embedded, seed, msgs.length, appId, flowSummary]);
  useEffect(() => {
    if (prevAppId.current === appId) return;
    if (prevAppId.current == null && appId) {
      try {
        localStorage.removeItem(convKey(prevAppId.current));
      } catch (e) {
        /* ignora */
      }
    }
    prevAppId.current = appId;
  }, [appId]);

  // hidrata a conversa salva quando o appId REAL chega (apps existentes montam
  // com appId null até o numericId resolver). Só carrega se não há conversa em
  // memória — para não sobrescrever um app novo recém-salvo (que migra acima).
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current || !appId) return;
    hydratedRef.current = true;
    if (msgs.length) return; // já há conversa em memória
    const restored = loadConv(appId);
    if (restored && restored.msgs.length) {
      convRef.current = restored.conv;
      setMsgs(restored.msgs);
      if (restored.usage) setUsage(restored.usage);
    }
  }, [appId, msgs.length]);

  // persiste a conversa ao final de cada turno (busy→false) e a cada mensagem.
  // NÃO apaga quando vazio (a limpeza é só via handleClear) — senão haveria
  // corrida com a hidratação: no render em que o appId chega, msgs ainda está
  // vazio e um removeItem apagaria a conversa salva antes de restaurá-la.
  useEffect(() => {
    if (busy) return; // espera o turno assentar (convRef já com tool_results)
    if (!msgs.length) return;
    try {
      localStorage.setItem(convKey(appId), JSON.stringify({ msgs, conv: convRef.current, usage }));
    } catch (e) {
      /* quota/serialização — ignora */
    }
  }, [msgs, busy, appId, usage]);

  const push = useCallback(m => setMsgs(prev => [...prev, m]), []);

  // mostra o widget de pergunta e devolve uma Promise que resolve com a resposta
  // do usuário (ou { stopped:true } se o loop for interrompido)
  const waitForAnswer = useCallback(
    spec =>
      new Promise(resolve => {
        askResolveRef.current = resolve;
        setPendingAsk(spec);
      }),
    []
  );

  const submitAnswer = useCallback(value => {
    const resolve = askResolveRef.current;
    askResolveRef.current = null;
    setPendingAsk(null);
    if (resolve) {
      const text = Array.isArray(value) ? value.join(", ") : String(value);
      resolve({ text, value });
    }
  }, []);

  // create_connector: abre o modal e devolve uma Promise que resolve com o
  // { connectorId, verbs } (ou { canceled:true }) quando o usuário salva/cancela.
  const waitForConnector = useCallback(
    type =>
      new Promise(resolve => {
        connResolveRef.current = resolve;
        setPendingConn({ type });
      }),
    []
  );
  const resolveConnector = useCallback(result => {
    const resolve = connResolveRef.current;
    connResolveRef.current = null;
    setPendingConn(null);
    if (resolve) resolve(result || { canceled: true });
  }, []);

  // o que o chat está mostrando AGORA, numa frase — o nó provisório do canvas usa o
  // mesmo texto (não um roteiro próprio que não bate com o chat)
  const lastMsg = msgs.length ? msgs[msgs.length - 1] : null;
  const statusText = !busy
    ? ""
    : pendingApproval
    ? "Aguardando sua confirmação"
    : pendingAsk
    ? "Aguardando sua resposta"
    : live.tool
    ? TOOL_LABEL[live.tool] || "Trabalhando"
    : lastMsg && lastMsg.role === "tool"
    ? lastMsg.text
    : thinkingShown || "Pensando";
  useEffect(() => {
    if (onStatus) onStatus(statusText);
  }, [statusText, onStatus]);

  // permissão explícita (STU-149): abre o modal com o resumo da ação e devolve uma
  // Promise que resolve true (confirmou) / false (cancelou ou parou o copiloto)
  const waitForApproval = useCallback(
    spec =>
      new Promise(resolve => {
        approvalResolveRef.current = resolve;
        setPendingApproval(spec);
      }),
    []
  );
  const resolveApproval = useCallback(ok => {
    const resolve = approvalResolveRef.current;
    approvalResolveRef.current = null;
    setPendingApproval(null);
    if (resolve) resolve(!!ok);
  }, []);
  // tools de CONTA (agente, evento do agente…) rodam no backend — as mesmas do MCP.
  // A lista (com o risco de cada uma) vem do contexto (/ai/account-tools).
  const accountToolOf = useCallback(name => ((ctxRef.current && ctxRef.current.accountTools) || []).find(t => t.name === name) || null, []);
  const permOf = useCallback(
    name => {
      const acct = accountToolOf(name);
      return permissionFor(name, acct && acct.risk !== "read" ? acct.risk : null);
    },
    [accountToolOf]
  );
  // o que o copiloto criou nesta conversa (agentes) + o fluxo liberado para alterar
  const createdAgentsRef = useRef(new Set());
  const flowApprovedRef = useRef(false);
  // agente "do copiloto": criado nesta conversa ou marcado como editável pela IA
  const agentOwned = useCallback(async agentId => {
    const id = Number(agentId);
    if (!id) return false;
    if (createdAgentsRef.current.has(id)) return true;
    try {
      const { data } = await api.get(`/applications/${id}`);
      return !!(data && data.aiEditable);
    } catch (e) {
      return false;
    }
  }, []);
  // decide se a ação precisa do "Confirmar" (null = roda direto)
  const needsApproval = useCallback(
    async (name, input) => {
      if (FLOW_EDIT_TOOLS.includes(name)) {
        if (!flowGuard || flowGuard.owned || flowApprovedRef.current) return null;
        return { ...FLOW_EDIT_PERMISSION(flowGuard.name), flowEdit: true };
      }
      if (name === "update_agent" && (await agentOwned(input && input.agentId))) return null;
      return permOf(name);
    },
    [flowGuard, agentOwned, permOf]
  );

  // TRAVA da conexão de WhatsApp (STU-180): só a regra no prompt não bastou — o
  // modelo, apressado, escolhia sozinho. Com MAIS DE UMA conexão ativa, o gatilho/
  // envio de WhatsApp é recusado até o USUÁRIO dizer qual (na conversa ou no
  // ask_user); sem nenhuma ativa, é recusado com a orientação de perguntar.
  const waChosenRef = useRef(null);
  const waGuard = useCallback((name, input) => {
    const a = input || {};
    const tt = String(a.trigger_type || "");
    const verb = String(a.verb || "");
    const isWaTrigger = name === "set_trigger" && /^event\.(whatsapp_qr|gupshup)\./.test(tt);
    const isWaSend = name === "add_node" && /^conn\.(whatsapp_qr|gupshup)\./.test(verb);
    if (!isWaTrigger && !isWaSend) return null;
    const conns = ((ctxRef.current && ctxRef.current.connectors) || []).filter(c => c.type === "whatsapp_qr" || c.type === "gupshup");
    const active = conns.filter(c => c.type !== "whatsapp_qr" || c.status === "conectado");
    const label = c => `#${c.id} "${c.name}" (${c.type === "whatsapp_qr" ? "QRCode" : "API Oficial"}${c.status ? `, ${c.status}` : ""})`;
    if (!active.length) {
      return {
        ok: false,
        error:
          `Não há conexão de WhatsApp ativa${conns.length ? ` (existe: ${conns.map(label).join("; ")})` : ""}. NÃO monte com ela: diga ao usuário que não há WhatsApp configurado/conectado e pergunte (ask_user) qual ele quer usar — WhatsApp QRCode (conecta lendo um QR em Integrações) ou API Oficial (Gupshup).`,
      };
    }
    if (active.length === 1) return null;
    const cid = Number((a.config && a.config.connectorId) || (a.params && a.params.connectorId) || a.connectorId) || null;
    if (waChosenRef.current && (!cid || cid === waChosenRef.current)) return null;
    // o usuário já disse qual? (mensagens dele + respostas do ask_user)
    const said = [];
    (convRef.current || []).forEach(m => {
      if (m.role !== "user") return;
      if (typeof m.content === "string") said.push(m.content);
      else (m.content || []).forEach(b => {
        try { const j = JSON.parse(b.content || "{}"); if (j && j.answer != null) said.push(String(j.answer)); } catch (e) { /* ignore */ }
      });
    });
    const txt = said.join(" ").toLowerCase();
    const picked = active.filter(c =>
      txt.includes(String(c.name || "").toLowerCase()) || txt.includes(`#${c.id}`) ||
      (c.type === "whatsapp_qr" ? /qr ?code|qr\b/.test(txt) : /oficial|gupshup/.test(txt))
    );
    if (picked.length === 1 && (!cid || cid === picked[0].id)) {
      waChosenRef.current = picked[0].id;
      return null;
    }
    return {
      ok: false,
      error:
        `A conta tem ${active.length} conexões de WhatsApp ativas: ${active.map(label).join("; ")}. ` +
        "PERGUNTE ao usuário qual usar (ask_user, com essas opções pelo nome) ANTES de definir o gatilho ou o envio — não escolha sozinho. Depois repita com o connectorId escolhido.",
    };
  }, []);

  const execTool = useCallback(
    async (name, input, decision) => {
      const blocked = waGuard(name, input);
      if (blocked) return blocked;
      if (!accountToolOf(name)) return runTool(name, input);
      try {
        const { data } = await api.post(`/ai/account-tools/${name}`, {
          args: input,
          decision,
          applicationId: appId && appId !== "new" ? appId : null,
          projectId: projectId || null, // fluxo novo ainda não tem id: o projeto vem da tela
        });
        // agente novo: link para abrir. NÃO mexe no contexto (ctx): ele faz parte das
        // instruções do especialista e mudar regravaria ~57 mil tokens de cache — o
        // id do agente volta ao especialista de automação pelo transfer_back.
        if (name === "create_agent" && data && data.id) {
          createdAgentsRef.current.add(Number(data.id));
          setMsgs(prev => [...prev, { role: "bot", text: `Agente **${data.name}** criado — [abrir em Agentes](/agents/${data.id})` }]);
        }
        return data;
      } catch (err) {
        return { ok: false, error: (err.response && err.response.data && err.response.data.error) || err.message };
      }
    },
    [accountToolOf, runTool, appId, projectId, waGuard]
  );

  // registro no backend do que a IA fez/pediu (best-effort)
  const logAction = useCallback(
    entry => api.post("/ai/actions", { applicationId: appId && appId !== "new" ? appId : null, ...entry }).catch(() => {}),
    [appId]
  );

  // loop agêntico em si (sem empurrar a msg do usuário) — reusado pelo "Tentar novamente"
  const loopRunningRef = useRef(false); // um laço por vez (dois em paralelo bagunçavam a conversa)
  const runLoop = useCallback(
    async () => {
      if (loopRunningRef.current) return;
      loopRunningRef.current = true;
      setBusy(true);
      stoppedRef.current = false;
      // teto de turnos (1 ferramenta por turno): fluxos grandes (bot com vários
      // submenus) precisam de MUITAS chamadas (add_node + connect). 16 era pouco
      // → o copiloto parava mudo no meio. 60 cobre builds grandes.
      const MAX_TURNS = 60;
      // quantas falhas SEGUIDAS da mesma ferramenta antes de desistir dela
      const MAX_TOOL_RETRIES = 2;
      let capReached = false;
      let haltReason = null; // erro determinístico: encerra o laço, não tenta de novo
      failCountRef.current = {};
      try {
        for (let turn = 0; turn < MAX_TURNS; turn++) {
          if (stoppedRef.current) break;
          abortRef.current = new AbortController();
          // system+tools do TURNO (a partir do contexto estável + extra do perfil).
          // Contexto estável na conversa → system idêntico entre turnos → cache OK.
          const system = profile.buildSystem(ctxRef.current, extraRef.current, { demo });
          const tools = profile.buildTools(ctxRef.current, extraRef.current, { demo });
          // eslint-disable-next-line no-await-in-loop
          // applicationId: o servidor guarda a transcrição por fluxo (antes a
          // conversa só existia no localStorage deste navegador). agent = o
          // especialista ativo (o servidor ajusta quanto o modelo pensa); parallel =
          // o perfil aceita várias ações por resposta.
          const chatBody = {
            system,
            tools,
            messages: (convRef.current = sanitizeConv(convRef.current)),
            applicationId: appId || null,
            agent: extraRef.current && extraRef.current.activeAgent,
            parallel: profile.parallelTools === true,
          };
          setLive({ text: "", tool: null });
          // eslint-disable-next-line no-await-in-loop
          const data = profile.stream === false
            ? (await api.post("/ai/chat", chatBody, { signal: abortRef.current.signal, timeout: COPILOT_TIMEOUT_MS })).data
            : await postChatStream(chatBody, {
                signal: abortRef.current.signal,
                onEvent: ev => {
                  if (ev.t === "text") setLive(l => ({ ...l, text: l.text + ev.d }));
                  else if (ev.t === "tool") setLive(l => ({ ...l, tool: ev.name }));
                },
              });
          setLive({ text: "", tool: null });
          // estatística da sessão, separando o que passou pelo cache do que não:
          //   input  = entrada NÃO-cacheada (nova + 1ª gravação) — preço cheio/1,25x
          //   cached = entrada LIDA do cache — ~0,1x
          //   output = saída
          if (data && data.usage) {
            const u = data.usage;
            setUsage(prev => ({
              input: prev.input + (u.input_tokens || 0) + (u.cache_creation_input_tokens || 0),
              cached: (prev.cached || 0) + (u.cache_read_input_tokens || 0),
              output: prev.output + (u.output_tokens || 0),
            }));
          }
          if (stoppedRef.current) break;
          const blocks = data.content || [];
          convRef.current.push({ role: "assistant", content: blocks });

          // resposta CORTADA no meio (max_tokens). O último bloco pode ser um
          // tool_use com JSON incompleto — executá-lo seria agir sobre um comando
          // truncado (o pior caso: um add_node/connect pela metade). Então NÃO
          // roda ferramenta nenhuma deste turno: mostra o texto que chegou, fecha
          // a conversa de forma VÁLIDA (tool_result "skipped" p/ todo tool_use
          // pendente, senão a API rejeita o próximo turno) e explica. "continue"
          // reenvia o contexto inteiro e o modelo refaz a parte cortada.
          if (data.stop_reason === "max_tokens") {
            for (const b of blocks) {
              if (b.type === "text" && b.text && b.text.trim()) push({ role: "bot", text: b.text.trim() });
            }
            const skipped = blocks
              .filter(b => b.type === "tool_use")
              .map(b => ({ type: "tool_result", tool_use_id: b.id, content: JSON.stringify({ skipped: true, reason: "resposta cortada (max_tokens)" }) }));
            if (skipped.length) convRef.current.push({ role: "user", content: skipped });
            push({ role: "bot", text: "A resposta ficou longa demais e foi cortada no meio — não executei nada truncado pra não deixar o fluxo quebrado. Diga **continue** que eu retomo de onde parei." });
            break;
          }

          const toolResults = [];
          for (const b of blocks) {
            // precisa do "Confirmar"? (regras de permissão — ver copilotPermissions)
            // eslint-disable-next-line no-await-in-loop
            const perm = b.type === "tool_use" && !stoppedRef.current ? await needsApproval(b.name, b.input || {}) : null;
            if (b.type === "text" && b.text && b.text.trim()) {
              push({ role: "bot", text: b.text.trim() });
            } else if (b.type === "tool_use" && b.name === "ask_user") {
              // pergunta estruturada: pausa o loop, mostra o widget e usa a
              // resposta do usuário como resultado da tool
              // eslint-disable-next-line no-await-in-loop
              const ans = await waitForAnswer({ id: b.id, ...(b.input || {}) });
              if (!ans.stopped) push({ role: "user", text: ans.text });
              toolResults.push({
                type: "tool_result",
                tool_use_id: b.id,
                content: JSON.stringify(ans.stopped ? { skipped: true } : { answer: ans.value }),
              });
              if (stoppedRef.current) break;
            } else if (b.type === "tool_use" && b.name === "create_connector") {
              // abre o modal de conector (credenciais só na UI, NUNCA na IA) e
              // devolve o id + os verbos nativos p/ o copiloto montar os nós
              // eslint-disable-next-line no-await-in-loop
              const r = await waitForConnector((b.input && b.input.type) || "");
              if (r && r.connectorId) push({ role: "bot", text: `Conector configurado: ${r.connectorName || r.connectorId}.` });
              toolResults.push({
                type: "tool_result",
                tool_use_id: b.id,
                content: JSON.stringify(
                  r && r.connectorId
                    ? { ok: true, connector_id: r.connectorId, connector_name: r.connectorName, verbs: r.verbs }
                    : { canceled: true }
                ),
              });
              if (stoppedRef.current) break;
            } else if (b.type === "tool_use" && perm) {
              // ação que exige o "Confirmar" (alterar o que não é do copiloto, apagar,
              // disparar algo real)
              const acct = accountToolOf(b.name);
              const input = b.input || {};
              // eslint-disable-next-line no-await-in-loop
              const approved = await waitForApproval({ ...perm, summary: perm.summary(input) });
              if (!approved) {
                logAction({ tool: b.name, risk: perm.risk, decision: "denied", args: input });
                push({ role: "tool", text: `${TOOL_LABEL[b.name] || b.name} — cancelado por você`, ok: false });
                toolResults.push({
                  type: "tool_result",
                  tool_use_id: b.id,
                  content: JSON.stringify({
                    denied: true,
                    message: "O usuário NÃO autorizou esta ação. Não tente de novo nem contorne; pergunte o que ele prefere.",
                  }),
                });
                if (stoppedRef.current) break;
                continue;
              }
              if (perm.flowEdit) flowApprovedRef.current = true; // fluxo: vale para a conversa toda
              // eslint-disable-next-line no-await-in-loop
              const result = await execTool(b.name, input, "approved");
              const failed = !!(result && (result.error || result.ok === false));
              // tool de conta: o backend já registrou; a do fluxo registra aqui
              if (!acct) logAction({ tool: b.name, risk: perm.risk, decision: "approved", args: input, ok: !failed, error: failed ? String((result && result.error) || "falhou") : null });
              if (result && result.__extra) extraRef.current = { ...extraRef.current, ...result.__extra };
              push({ role: "tool", text: TOOL_LABEL[b.name] || b.name, ok: !failed, ...(failed ? { error: errorText(result) } : {}) });
              toolResults.push({
                type: "tool_result",
                tool_use_id: b.id,
                content: JSON.stringify(result == null ? { ok: true } : result),
              });
            } else if (b.type === "tool_use") {
              // sinaliza que o fluxo mudou de ESTRUTURA (p/ reorganizar o canvas)
              if (["add_node", "connect", "delete_node"].includes(b.name)) structuralRef.current = true;
              // eslint-disable-next-line no-await-in-loop
              const result = await execTool(b.name, b.input || {});
              // a tool pode alterar o estado do perfil (ex.: desbloquear ferramentas)
              // → afeta system/tools do PRÓXIMO turno
              if (result && result.__extra) extraRef.current = { ...extraRef.current, ...result.__extra };
              const failed = !!(result && (result.error || result.ok === false));
              const errText = failed ? errorText(result) : "";
              push({
                role: "tool",
                text: TOOL_LABEL[b.name] || b.name,
                node: b.input && b.input.verb ? String(b.input.verb) : "",
                ok: !failed,
                ...(failed ? { error: errText } : {}),
              });
              // guarda o erro no registro de ações da IA (diagnóstico depois)
              if (failed && !accountToolOf(b.name)) logAction({ tool: b.name, risk: "write", args: b.input || {}, ok: false, error: errText });
              // DISJUNTOR. Um erro determinístico (limite de plano) não melhora
              // com nova tentativa, mas o modelo tende a "consertar" o fluxo e
              // tentar de novo — laço que só queima token. Duas condições param:
              // a ferramenta marcar retryable:false, ou a MESMA ferramenta falhar
              // repetidamente. Não depende do modelo desistir sozinho.
              if (failed) {
                failCountRef.current[b.name] = (failCountRef.current[b.name] || 0) + 1;
              } else {
                failCountRef.current[b.name] = 0;
              }
              const terminal = result && result.retryable === false;
              const repeated = (failCountRef.current[b.name] || 0) >= MAX_TOOL_RETRIES;
              if (terminal || repeated) {
                haltReason =
                  (result && result.error) ||
                  `A ação "${TOOL_LABEL[b.name] || b.name}" falhou ${failCountRef.current[b.name]}x seguidas.`;
              }
              toolResults.push({
                type: "tool_result",
                tool_use_id: b.id,
                content: JSON.stringify(result == null ? { ok: true } : result),
              });
            }
          }

          // ao parar no meio, garante tool_result p/ TODO tool_use (conv válida)
          if (stoppedRef.current) {
            const have = new Set(toolResults.map(t => t.tool_use_id));
            blocks
              .filter(b => b.type === "tool_use" && !have.has(b.id))
              .forEach(b => toolResults.push({ type: "tool_result", tool_use_id: b.id, content: JSON.stringify({ skipped: true }) }));
            if (toolResults.length) convRef.current.push({ role: "user", content: toolResults });
            break;
          }

          if (data.stop_reason === "tool_use" && toolResults.length) {
            convRef.current.push({ role: "user", content: toolResults });
            // erro que não melhora com nova tentativa: fecha a conversa aqui, com
            // o motivo na tela. Sem isto o modelo continuava tentando até o teto
            // de 60 turnos, gastando token à toa.
            if (haltReason) {
              push({ role: "assistant", text: haltReason });
              break;
            }
            if (turn === MAX_TURNS - 1) capReached = true; // último turno e ainda quer continuar
            continue; // let the model see results and keep building
          }
          break;
        }
        // pausou pelo teto de turnos (fluxo grande) — o histórico está completo,
        // então "continue" retoma exatamente de onde parou
        if (capReached && !stoppedRef.current) {
          push({ role: "bot", text: "O fluxo ficou grande e eu pausei aqui pra não travar. Diga **continue** que eu termino de montar e conectar o resto." });
        }
      } catch (err) {
        // ignora cancelamento (botão Parar)
        const canceled = stoppedRef.current || err.code === "ERR_CANCELED" || err.name === "CanceledError" || err.message === "canceled";
        if (!canceled) {
          const timedOut = err.code === "ECONNABORTED" || /timeout/i.test(err.message || "");
          const m = timedOut
            ? "A IA demorou demais para responder (tempo limite)."
            : (err.response && err.response.data && err.response.data.error) || err.message;
          push({ role: "err", text: m || "Falha no copiloto." });
        }
      } finally {
        loopRunningRef.current = false;
        abortRef.current = null;
        setLive({ text: "", tool: null });
        setBusy(false);
        if (onAfterRun) onAfterRun(structuralRef.current);
        structuralRef.current = false;
      }
    },
    [push, profile, demo, onAfterRun, waitForAnswer, waitForConnector, waitForApproval, logAction, needsApproval, accountToolOf, execTool]
  );

  const run = useCallback(
    async text => {
      const q = (text || "").trim();
      if (!q || busy) return;
      setInput("");
      push({ role: "user", text: q });
      convRef.current.push({ role: "user", content: q });
      runLoop();
    },
    [busy, push, runLoop]
  );

  // "Tentar novamente" após um erro: remove os quadrinhos de erro e re-executa o
  // loop com a conversa atual (a msg do usuário já está no histórico).
  const retry = useCallback(() => {
    if (busy) return;
    setMsgs(prev => prev.filter(m => m.role !== "err"));
    runLoop();
  }, [busy, runLoop]);

  // para o loop agêntico em andamento (aborta a requisição em voo + resolve uma
  // pergunta em aberto para o loop poder encerrar limpo)
  const handleStop = useCallback(() => {
    stoppedRef.current = true;
    if (abortRef.current) abortRef.current.abort();
    if (askResolveRef.current) {
      const resolve = askResolveRef.current;
      askResolveRef.current = null;
      setPendingAsk(null);
      resolve({ stopped: true });
    }
    if (connResolveRef.current) {
      const resolve = connResolveRef.current;
      connResolveRef.current = null;
      setPendingConn(null);
      resolve({ canceled: true });
    }
    if (approvalResolveRef.current) {
      const resolve = approvalResolveRef.current;
      approvalResolveRef.current = null;
      setPendingApproval(null);
      resolve(false);
    }
    setBusy(false);
  }, []);

  // seed vindo do chat (handoff): dispara a construção 1x quando abre, já com o
  // contexto da conta carregado
  const seededRef = useRef(false);

  // "Nova conversa": limpa display, histórico do provider e o que está salvo
  const handleClear = useCallback(() => {
    if (busy) handleStop();
    setPendingAsk(null);
    askResolveRef.current = null;
    setPendingConn(null);
    connResolveRef.current = null;
    convRef.current = [];
    waChosenRef.current = null;
    extraRef.current = baseExtra(); // volta ao baseline (ex.: re-trava tools)
    flowApprovedRef.current = false; // nova conversa: pede de novo para alterar fluxo alheio
    seededRef.current = true; // não re-semear o handoff após limpar
    setMsgs([]);
    setUsage({ input: 0, output: 0, cached: 0 });
    try {
      localStorage.removeItem(convKey(appId));
    } catch (e) {
      /* ignora */
    }
  }, [busy, handleStop, appId]);

  useEffect(() => {
    if (open && seed && ctx && !seededRef.current && !msgs.length) {
      seededRef.current = true;
      run(seed);
    }
  }, [open, seed, ctx, msgs.length, run]);

  // recolhido → FAB (canto inf. direito, sempre visível enquanto o builder abre)
  // entrada "expande do canto" só na 1ª abertura vinda da home (com seed)
  const seedEntrance = open && !!seed && !seedEnteredRef.current;

  // --- redimensionar a caixa flutuante -------------------------------------
  // Limites: mínimo para o chat continuar utilizável; máximo em viewport para a
  // caixa nunca sair da tela (inclusive se o usuário diminuir a janela depois).
  const MIN_W = 320;
  const MIN_H = 360;
  const maxW = () => Math.round(window.innerWidth * 0.92);
  const maxH = () => Math.round(window.innerHeight * 0.9);
  const clampBox = s => ({
    w: Math.max(MIN_W, Math.min(s.w, maxW())),
    h: Math.max(MIN_H, Math.min(s.h, maxH())),
  });

  // Se a janela encolher (ou o tamanho salvo vier de um monitor maior), a caixa
  // sairia da tela. Reaplica os limites a cada resize do viewport.
  useEffect(() => {
    const onWinResize = () =>
      setBoxSize(s => {
        const w = Math.max(MIN_W, Math.min(s.w, Math.round(window.innerWidth * 0.92)));
        const h = Math.max(MIN_H, Math.min(s.h, Math.round(window.innerHeight * 0.9)));
        return w === s.w && h === s.h ? s : { w, h }; // mesma referência = sem re-render
      });
    // a posição também precisa caber: salva num monitor largo, a caixa ficaria
    // fora da tela numa janela menor.
    const onWinResizePos = () =>
      setBoxPos(p => {
        const right = Math.max(0, Math.min(p.right, Math.max(0, window.innerWidth - MIN_W)));
        const bottom = Math.max(0, Math.min(p.bottom, Math.max(0, window.innerHeight - MIN_H)));
        return right === p.right && bottom === p.bottom ? p : { right, bottom };
      });
    onWinResize(); // também no 1º render: o valor salvo pode já não caber
    onWinResizePos();
    window.addEventListener("resize", onWinResizePos);
    window.addEventListener("resize", onWinResize);
    return () => {
      window.removeEventListener("resize", onWinResize);
      window.removeEventListener("resize", onWinResizePos);
    };
  }, []);

  // wrapper comum de arrasto: captura o ponteiro (senão o gesto trava ao passar
  // por cima do canvas do builder), escuta no próprio alvo e persiste no fim.
  const drag = (e, onMove, persist) => {
    e.preventDefault();
    const target = e.currentTarget;
    try { target.setPointerCapture(e.pointerId); } catch (err) { /* navegador antigo */ }
    const onUp = () => {
      target.removeEventListener("pointermove", onMove);
      target.removeEventListener("pointerup", onUp);
      persist();
    };
    target.addEventListener("pointermove", onMove);
    target.addEventListener("pointerup", onUp);
  };

  // `dir` combina os lados arrastados: n, s, e, w, ne, nw, se, sw.
  //
  // O CSS ancora a caixa em right/bottom. Arrastar a borda ESQUERDA ou SUPERIOR
  // só muda o tamanho — a âncora é justamente o lado oposto. Já arrastar a borda
  // DIREITA ou INFERIOR move a própria âncora, então tamanho e posição mudam
  // juntos; senão a caixa deslizaria em vez de esticar.
  //
  // A regra que mantém tudo coerente: o lado OPOSTO ao arrastado fica parado.
  // Por isso o cálculo parte das coordenadas absolutas das bordas, e o clamp de
  // tamanho não faz a caixa "escorregar" ao bater no mínimo.
  const startResize = (e, dir) => {
    const startLeft = window.innerWidth - boxPos.right - boxSize.w;
    const startTop = window.innerHeight - boxPos.bottom - boxSize.h;
    const start = { x: e.clientX, y: e.clientY, w: boxSize.w, h: boxSize.h, ...boxPos };
    drag(
      e,
      ev => {
        const dx = ev.clientX - start.x;
        const dy = ev.clientY - start.y;
        let w = start.w;
        let h = start.h;
        let right = start.right;
        let bottom = start.bottom;

        if (dir.includes("w")) w = start.w - dx; // borda esquerda: direita fica parada
        if (dir.includes("e")) w = start.w + dx; // borda direita: esquerda fica parada
        if (dir.includes("n")) h = start.h - dy;
        if (dir.includes("s")) h = start.h + dy;

        const size = clampBox({ w, h });
        // recalcula a âncora só nos lados que a movem, já com o tamanho limitado
        if (dir.includes("e")) right = Math.max(0, window.innerWidth - startLeft - size.w);
        if (dir.includes("s")) bottom = Math.max(0, window.innerHeight - startTop - size.h);

        setBoxSize(size);
        setBoxPos({ right, bottom });
      },
      () => {
        setBoxSize(s => { try { localStorage.setItem(LS_BOX, JSON.stringify(s)); } catch (err) { /* modo privado */ } return s; });
        setBoxPos(p => { try { localStorage.setItem(LS_POS, JSON.stringify(p)); } catch (err) { /* modo privado */ } return p; });
      }
    );
  };

  // redimensiona a COLUNA (modo column) arrastando a borda DIREITA: a esquerda fica
  // ancorada no rail; largura entre 320 e 720. Persiste no fim.
  const startColResize = e => {
    const start = { x: e.clientX, w: colW };
    drag(
      e,
      ev => setColW(Math.max(320, Math.min(720, start.w + (ev.clientX - start.x)))),
      () => setColW(w => { try { localStorage.setItem("nb_copilot_col_w", String(w)); } catch (err) { /* modo privado */ } return w; })
    );
  };

  // mover pelo cabeçalho. Mantém a âncora right/bottom do CSS — mexer em
  // left/top exigiria converter a posição e brigaria com o redimensionamento.
  const startMove = e => {
    // o cabeçalho tem botões (fechar, limpar…); não sequestrar o clique deles
    if (e.target.closest && e.target.closest("button")) return;
    const start = { x: e.clientX, y: e.clientY, ...boxPos };
    drag(
      e,
      ev => {
        // arrastar p/ direita diminui `right`; p/ baixo diminui `bottom`
        const right = start.right - (ev.clientX - start.x);
        const bottom = start.bottom - (ev.clientY - start.y);
        // trava nas bordas: a caixa inteira continua visível
        setBoxPos({
          right: Math.max(0, Math.min(right, window.innerWidth - boxSize.w)),
          bottom: Math.max(0, Math.min(bottom, window.innerHeight - boxSize.h)),
        });
      },
      () => setBoxPos(p => { try { localStorage.setItem(LS_POS, JSON.stringify(p)); } catch (err) { /* modo privado */ } return p; })
    );
  };

  if (!open) {
    if (inline) return null; // embutido/coluna: o pai controla a visibilidade (sem FAB)
    return (
      <button type="button" className={classes.fab} onClick={onOpen} title="Copiloto">
        <Sparkle size={22} />
      </button>
    );
  }

  // empty-state: NÃO mostra o estático (genérico) antes de resolver o dinâmico.
  // "resolving" = o Builder ainda carrega o fluxo (flowLoading) OU as sugestões de um
  // fluxo existente ainda não voltaram → mostra só o placeholder "lendo o fluxo…".
  // showFlowText = mostra o texto voltado ao fluxo (otimista durante o load, e sempre
  // que há fluxo) em vez do "descreva o fluxo que você quer". dynExamples: null =
  // pendente · [] = resolvido (usa genéricos) · [itens] = contextuais.
  // envia + marca a dica do Enter como vista (some depois da 1ª vez)
  const submitChat = () => {
    if (!enterHintSeen) { setEnterHintSeen(true); try { localStorage.setItem("nb_copilot_enterhint", "1"); } catch (e) { /* ignore */ } }
    run(input);
  };
  const resolving = msgs.length === 0 && (!!flowLoading || (!!flowSummary && dynExamples === null));
  const showFlowText = !!flowLoading || !!flowSummary;
  const emptyExamples = dynExamples && dynExamples.length ? dynExamples : (profile.examples || []);
  const emptyKey = resolving ? "loading" : dynExamples && dynExamples.length ? "ctx" : flowSummary ? "flow" : "new";
  // HERO: só enquanto não há conversa nem processamento. Ao enviar (inclusive seed
  // auto-enviado), vira chat normal; a largura só inverte quando o 1º nó cai (Builder).
  const heroActive = !!hero && msgs.length === 0 && !busy;

  return (
    <div
      className={clsx(column ? classes.boxColumn : embedded ? classes.boxEmbedded : classes.box, !inline && seedEntrance && classes.boxSeed)}
      // coluna: largura redimensionável (persistida). Drawer: o pai manda. Flutuante: box/pos.
      style={column ? { width: widthOverride != null ? widthOverride : colW } : embedded ? undefined : { width: boxSize.w, height: boxSize.h, right: boxPos.right, bottom: boxPos.bottom }}
    >
      {!inline &&
        ["n", "s", "w", "e", "nw", "ne", "sw", "se"].map(dir => (
          <div
            key={dir}
            className={clsx(classes.rs, classes[`rs${dir.toUpperCase()}`])}
            onPointerDown={e => startResize(e, dir)}
          />
        ))}
      {/* coluna: alça de redimensionar na BORDA DIREITA (arrasta p/ esquerda/direita).
          No hero/transição a largura é controlada (widthOverride) → sem alça. */}
      {column && widthOverride == null && <div className={classes.colResize} onPointerDown={startColResize} />}
      {!heroActive && (
      <div
        className={clsx(classes.head, !inline && classes.headDrag)}
        onPointerDown={inline ? undefined : startMove}
      >
        <div className={classes.avatar}><Sparkle size={14} /></div>
        <span className={classes.title}>{profile.title}</span>
        <span className={classes.ctxChip}>{profile.ctxChip}</span>
        <div className={classes.headSpacer} />
        {msgs.length > 0 && (
          <IconButton size="small" className={classes.headBtn} onClick={handleClear} title="Nova conversa">
            <AddCommentOutlinedIcon fontSize="small" />
          </IconButton>
        )}
        {/* COLUNA: sem minimizar/expandir/fechar. Só um "recolher" que aparece abaixo
            de 1280 (única forma de esconder o chat). Fora do modo coluna, mantém
            minimizar/fechar (flutuante e drawer). */}
        {column ? (
          <IconButton size="small" className={clsx(classes.headBtn, classes.collapseBtnNarrow)} onClick={onClose} title="Recolher">
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        ) : (
          <>
            <IconButton size="small" className={classes.headBtn} onClick={onClose} title="Minimizar">
              <MinimizeIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" className={classes.headBtn} onClick={onClose} title="Fechar">
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        )}
      </div>
      )}

      <div className={classes.body} ref={bodyRef}>
        {heroActive ? (
          // HERO (nova automação): espelha a tela de nova conversa. O campo é o
          // próprio input do copiloto; os cards disparam run() direto (sem navegar).
          <div className={clsx(classes.hero, classes.emptyFade)} key="hero">
            <div className={classes.heroSpark}><Sparkle size={21} /></div>
            <h1 className={classes.heroH1}>O que você quer montar?</h1>
            <p className={classes.heroLead}>Descreva em uma frase. A Numera monta na sua frente — sem configurar nada agora.</p>
            <div className={classes.heroField}>
              <textarea
                ref={el => {
                  inputRef.current = el;
                  heroInputRef.current = el;
                }}
                className={classes.heroTextarea}
                rows={1}
                placeholder="Ex.: atender ligações com uma IA que agenda meus clientes"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submitChat(); } }}
                autoFocus
              />
              <button
                type="button"
                className={clsx(classes.heroSend, !input.trim() && classes.heroSendOff)}
                disabled={!input.trim()}
                onClick={submitChat}
                aria-label="Enviar"
              >
                <SendIcon />
              </button>
            </div>
            <div className={classes.seedsLabel}>Ou comece por um destes:</div>
            <div className={classes.seedList}>
              {SEEDS.map(s => (
                <button key={s.label} type="button" className={classes.seedCard} onClick={() => run(s.send)}>
                  <span className={classes.seedIcon} style={{ background: hexA(s.color, 0.12), color: s.color }}>
                    <s.icon />
                  </span>
                  <span className={classes.seedText}>{s.label}</span>
                  <span className={classes.seedArrow}><ArrowFwdIcon /></span>
                </button>
              ))}
            </div>
            {onManual && (
              <button type="button" className={classes.heroManual} onClick={onManual}>Prefiro montar manualmente →</button>
            )}
          </div>
        ) : msgs.length === 0 ? (
          // key muda quando o contexto do fluxo resolve → o fade replaz suavemente
          // (evita o "carrega errado e troca abrupto").
          <div className={clsx(classes.empty, classes.emptyFade)} key={showFlowText ? "flow" : "new"}>
            <div className={classes.emptyTitle}>{showFlowText ? "Como posso ajudar neste fluxo?" : profile.emptyTitle}</div>
            <div>{showFlowText ? "Ajusto, testo ou explico o que ele faz — ou é só pedir uma mudança." : profile.emptyDesc}</div>
          </div>
        ) : null}
        {(() => {
          // agrupa mensagens 'tool' CONSECUTIVAS num único bloco colapsável
          const items = [];
          for (let i = 0; i < msgs.length; i++) {
            if (msgs[i].role === "tool") {
              const tools = [];
              const start = i;
              while (i < msgs.length && msgs[i].role === "tool") tools.push(msgs[i++]);
              i--;
              items.push({ kind: "tools", tools, start });
            } else {
              items.push({ kind: "msg", m: msgs[i], i });
            }
          }
          const lastIsTools = items.length > 0 && items[items.length - 1].kind === "tools";
          const out = items.map((it, idx) => {
            if (it.kind === "tools") {
              const live = busy && !pendingAsk && idx === items.length - 1;
              return <ToolGroup key={`tg-${it.start}`} tools={it.tools} live={live} />;
            }
            const { m, i } = it;
            if (m.role === "user") return <div key={i} className={classes.userMsg}>{m.text}</div>;
            if (m.role === "bot") {
              return (
                <div key={i} className={classes.botRow}>
                  <div className={classes.botAvatar}><Sparkle size={13} /></div>
                  <div className={classes.botMsg}><Markdown text={m.text} /></div>
                </div>
              );
            }
            if (m.role === "err")
              return (
                <div key={i} className={classes.err}>
                  <div>{m.text}</div>
                  <button type="button" className={classes.errRetry} onClick={retry} disabled={busy}>
                    <ReplayIcon /> Tentar novamente
                  </button>
                </div>
              );
            return null;
          });
          // "pensando" só quando NÃO há um grupo de ações ativo pra animar (início do
          // turno, antes da 1ª ação, ou logo após um texto do bot): indicador de
          // digitação (avatar + pontinhos), não o card antigo.
          // streaming: o texto da IA aparece enquanto é gerado
          if (busy && !pendingAsk && live.text.trim()) {
            out.push(
              <div key="live" className={classes.botRow}>
                <div className={classes.botAvatar}><Sparkle size={13} /></div>
                <div className={classes.botMsg}><Markdown text={live.text} /></div>
              </div>
            );
          } else if (busy && !pendingAsk && !lastIsTools) {
            out.push(
              <div key="thinking" className={classes.botRow}>
                <div className={classes.botAvatar}><Sparkle size={13} /></div>
                <div className={classes.botMsg}><ThinkingStatus label={live.tool ? TOOL_LABEL[live.tool] || null : null} onShown={setThinkingShown} /></div>
              </div>
            );
          }
          return out;
        })()}
        {pendingAsk && <AskWidget spec={pendingAsk} onSubmit={submitAnswer} />}
        {pendingConn && <ConnectorQuickModal type={pendingConn.type} onDone={resolveConnector} />}
        {pendingApproval && (
          <Dialog open onClose={() => resolveApproval(false)} maxWidth="xs" fullWidth>
            <DialogTitle>{pendingApproval.title}</DialogTitle>
            <DialogContent>
              <div className={classes.approvalText}>{pendingApproval.summary}</div>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => resolveApproval(false)}>Cancelar</Button>
              <Button onClick={() => resolveApproval(true)} variant="contained" color="secondary" autoFocus>
                {pendingApproval.confirm || "Confirmar"}
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </div>

      {/* ainda resolvendo (carregando o fluxo ou as sugestões): placeholder, não os genéricos */}
      {!heroActive && msgs.length === 0 && resolving && (
        <div className={clsx(classes.chips, classes.emptyFade)} key="sugg-loading">
          <span className={classes.suggLoading}>lendo o fluxo…</span>
        </div>
      )}
      {!heroActive && msgs.length === 0 && !resolving && emptyExamples.length > 0 && (
        <div className={clsx(classes.chips, classes.emptyFade)} key={emptyKey}>
          {emptyExamples.map((e, i) => (
            <button key={i} className={classes.chip} onClick={() => run(e)}>{e}</button>
          ))}
        </div>
      )}

      {/* respostas rápidas durante a conversa (item 61) */}
      {msgs.length > 0 && !busy && !pendingAsk && (profile.quickReplies || []).length > 0 && (
        <div className={classes.chips}>
          {profile.quickReplies.map((r, i) => (
            <button key={i} className={classes.chip} onClick={() => run(r.prompt)}>{r.label}</button>
          ))}
        </div>
      )}

      {!heroActive && (
      <div className={classes.foot}>
        {canSeeTokens && (usage.input || usage.output) > 0 && (
          <div className={classes.tokLine} title="Tokens consumidos nesta sessão do copiloto (acumulado) — visível só para staff da Numera">
            sessão — entrada: {fmtK(usage.input)} · cache: {fmtK(usage.cached || 0)} · saída: {fmtK(usage.output)} · ~{fmtBRL(costBRL(usage))}
          </div>
        )}
        <div className={clsx(classes.inputRow, pulsing && classes.inputRowPulse)}>
          <MicIcon className={classes.micIcon} />
          <InputBase
            className={classes.input}
            inputRef={inputRef}
            placeholder={busy ? "Escreva enquanto processa…" : flowSummary ? "Peça uma alteração ou pergunte sobre este fluxo" : profile.placeholder}
            multiline
            minRows={2}
            maxRows={8}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submitChat(); // run IGNORA enquanto busy → não envia processando
              }
            }}
          />
          {/* Stop é um botão SEPARADO (não o de enviar). O usuário pode escrever
              durante o processamento; enviar fica DESABILITADO até terminar ou Stop. */}
          {busy && (
            <IconButton className={classes.stop} size="small" onClick={handleStop} title="Parar">
              <StopIcon fontSize="small" />
            </IconButton>
          )}
          <IconButton
            className={classes.send}
            size="small"
            disabled={busy || !input.trim()}
            onClick={submitChat}
            title="Enviar"
          >
            <SendIcon fontSize="small" />
          </IconButton>
        </div>
        {!enterHintSeen && !busy && (
          <div className={classes.enterHint}>Enter envia · Shift+Enter quebra linha</div>
        )}
      </div>
      )}
    </div>
  );
};

export default CopilotPanel;
